import {
  AdminCreateOatfiPaymentMethod,
  AdminFetchOatfiBusiness,
  AdminFetchPlaidLinkToken,
  AdminRequestOatfiUnderwriting,
  AdminSaveOatfiBusiness,
  AdminUploadHistoricalInvoices,
  AdminUploadOatfiSupplementalData,
  ClearAdminOatfiPlaidLink,
} from 'src/constants/actionTypes'
import type { OatfiBusiness, PlaidLink } from 'src/interfaces/oatfiBusiness'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import type { Action } from 'src/utils/reduxUtils'
import type { OatfiBusinessState } from './types'

const getInitialState = (): OatfiBusinessState => {
  return {
    partnerId: null,
    business: null,
    loading: false,
    loaded: false,
    error: null,
    saving: false,
    requestingUnderwriting: false,
    uploadingSupplemental: false,
    uploadingHistorical: false,
    creatingPaymentMethod: false,
    generatingPlaidLink: false,
    plaidLink: null,
  }
}

/**
 * Whether a completion belongs to the customer currently on screen.
 *
 * A completion for a customer that is no longer selected must leave this slice **entirely**
 * untouched — including that operation's in-flight flag. Clearing the flag here would clear it
 * for whatever the *current* customer has in flight, re-enabling their button mid-request and
 * letting the same mutation be submitted twice. The stuck-flag case this might otherwise guard
 * against is already handled at REQUEST time, which resets every flag on a partner change.
 */
function isForSelectedPartner(payload: unknown, statePartnerId: string | null): boolean {
  const typed = payload as { partnerId?: string } | undefined

  return typed !== undefined && typed.partnerId === statePartnerId
}

/**
 * Reads the `business` out of a `{ partnerId, business }` success payload, or returns null when
 * the payload carries none. Callers must check `isForSelectedPartner` first — this deliberately
 * does not re-check the partner, so that "wrong customer" and "no business in the payload" stay
 * distinguishable: the former must not touch the slice, the latter must still clear its flag.
 */
function readBusinessPayload(payload: unknown): OatfiBusiness | null {
  const typed = payload as { business?: OatfiBusiness } | undefined

  if (typed === undefined || !typed.business) {
    return null
  }

  return typed.business
}

export const oatfiBusinessReducer = (
  state: OatfiBusinessState = getInitialState(),
  action: Action<unknown>,
): OatfiBusinessState => {
  switch (action.type) {
    case AdminFetchOatfiBusiness.type.REQUEST: {
      const partnerId = (action.payload as { partnerId?: string } | undefined)?.partnerId
      const isSamePartner = Boolean(partnerId) && state.partnerId === partnerId

      // The in-flight flags are reset on a partner change because every SUCCESS case below is
      // partner-guarded: a mutation for the previous customer that resolves after the switch
      // returns early and never clears its own flag. Without this reset the new customer's page
      // inherits a stuck `true` and keeps that card's button disabled forever — the submit that
      // would clear the flag is itself the disabled control.
      return {
        ...state,
        partnerId: partnerId ?? state.partnerId,
        business: isSamePartner ? state.business : null,
        loaded: isSamePartner ? state.loaded : false,
        saving: isSamePartner ? state.saving : false,
        requestingUnderwriting: isSamePartner ? state.requestingUnderwriting : false,
        uploadingSupplemental: isSamePartner ? state.uploadingSupplemental : false,
        uploadingHistorical: isSamePartner ? state.uploadingHistorical : false,
        creatingPaymentMethod: isSamePartner ? state.creatingPaymentMethod : false,
        generatingPlaidLink: isSamePartner ? state.generatingPlaidLink : false,
        // A hosted bank-link URL is customer-specific and short-lived. Leaving the previous
        // customer's link on screen is the failure the partnerId guard exists to prevent.
        plaidLink: isSamePartner ? state.plaidLink : null,
        loading: true,
        error: null,
      }
    }
    case AdminFetchOatfiBusiness.type.SUCCESS: {
      const payload = action.payload as
        | { partnerId: string; business: OatfiBusiness | null }
        | undefined

      if (payload === undefined || payload.partnerId !== state.partnerId) {
        return state
      }

      // `business: null` is a real loaded result — the GET 404s for a partner with no row.
      return {
        ...state,
        business: payload.business,
        loading: false,
        loaded: true,
        error: null,
      }
    }
    case AdminFetchOatfiBusiness.type.FAILED:
      return {
        ...state,
        loading: false,
        error: resolveErrorMessage(action.payload, 'Failed to load the Oatfi business'),
      }

    case AdminSaveOatfiBusiness.type.REQUEST:
      return {
        ...state,
        saving: true,
      }
    case AdminSaveOatfiBusiness.type.SUCCESS: {
      // A completion for a deselected customer leaves the slice untouched — see
      // isForSelectedPartner. Clearing `saving` here would clear it for the customer now on
      // screen, re-enabling their button mid-request.
      if (!isForSelectedPartner(action.payload, state.partnerId)) {
        return state
      }

      const business = readBusinessPayload(action.payload)

      if (business === null) {
        return { ...state, saving: false }
      }

      return {
        ...state,
        business,
        loaded: true,
        saving: false,
      }
    }
    case AdminSaveOatfiBusiness.type.FAILED:
      return {
        ...state,
        saving: false,
      }

    case AdminRequestOatfiUnderwriting.type.REQUEST:
      return {
        ...state,
        requestingUnderwriting: true,
      }
    case AdminRequestOatfiUnderwriting.type.SUCCESS: {
      // A completion for a deselected customer leaves the slice untouched — see
      // isForSelectedPartner. Clearing `requestingUnderwriting` here would clear it for the customer now on
      // screen, re-enabling their button mid-request.
      if (!isForSelectedPartner(action.payload, state.partnerId)) {
        return state
      }

      const business = readBusinessPayload(action.payload)

      if (business === null) {
        return { ...state, requestingUnderwriting: false }
      }

      return {
        ...state,
        business,
        requestingUnderwriting: false,
      }
    }
    case AdminRequestOatfiUnderwriting.type.FAILED:
      return {
        ...state,
        requestingUnderwriting: false,
      }

    case AdminCreateOatfiPaymentMethod.type.REQUEST:
      return {
        ...state,
        creatingPaymentMethod: true,
      }
    case AdminCreateOatfiPaymentMethod.type.SUCCESS: {
      // A completion for a deselected customer leaves the slice untouched — see
      // isForSelectedPartner. Clearing `creatingPaymentMethod` here would clear it for the customer now on
      // screen, re-enabling their button mid-request.
      if (!isForSelectedPartner(action.payload, state.partnerId)) {
        return state
      }

      const business = readBusinessPayload(action.payload)

      if (business === null) {
        return { ...state, creatingPaymentMethod: false }
      }

      return {
        ...state,
        business,
        creatingPaymentMethod: false,
      }
    }
    case AdminCreateOatfiPaymentMethod.type.FAILED:
      return {
        ...state,
        creatingPaymentMethod: false,
      }

    // Supplemental uploads are a pass-through to Oatfi with no read endpoint, so a success
    // changes nothing readable — it only clears its own flag.
    case AdminUploadOatfiSupplementalData.type.REQUEST:
      return {
        ...state,
        uploadingSupplemental: true,
      }
    case AdminUploadOatfiSupplementalData.type.SUCCESS:
    case AdminUploadOatfiSupplementalData.type.FAILED:
      return {
        ...state,
        uploadingSupplemental: false,
      }

    // Historical invoices land on the Invoice tab's list, not on this slice.
    case AdminUploadHistoricalInvoices.type.REQUEST:
      return {
        ...state,
        uploadingHistorical: true,
      }
    case AdminUploadHistoricalInvoices.type.SUCCESS:
    case AdminUploadHistoricalInvoices.type.FAILED:
      return {
        ...state,
        uploadingHistorical: false,
      }

    case AdminFetchPlaidLinkToken.type.REQUEST:
      return {
        ...state,
        generatingPlaidLink: true,
      }
    case AdminFetchPlaidLinkToken.type.SUCCESS: {
      const payload = action.payload as { partnerId: string; link: PlaidLink } | undefined

      if (payload === undefined || payload.partnerId !== state.partnerId) {
        return { ...state, generatingPlaidLink: false }
      }

      return {
        ...state,
        plaidLink: payload.link,
        generatingPlaidLink: false,
      }
    }
    case AdminFetchPlaidLinkToken.type.FAILED:
      return {
        ...state,
        generatingPlaidLink: false,
      }

    case ClearAdminOatfiPlaidLink.type:
      return {
        ...state,
        plaidLink: null,
      }

    default:
      return state
  }
}
